import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png";

const getAgeBracket = (age) => age <= 4 ? 'small' : 'large';

const NewsletterQuizPage = () => {
  const { newsletterId } = useParams();
  const [searchParams] = useSearchParams();
  const childIdFromUrl = searchParams.get('child_id');
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsletterQuestion, setNewsletterQuestion] = useState('');
  const [childName, setChildName] = useState('');
  const [shakeIdx, setShakeIdx] = useState(null);
  const [showBravo, setShowBravo] = useState(false);
  const [allChildNames, setAllChildNames] = useState('');
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadAndAutoSelect();
    } else {
      setLoading(false);
      setError('connect');
    }
  }, [isAuthenticated]);

  const loadAndAutoSelect = async () => {
    try {
      const res = await api.get('/children');
      const kids = res.data.children || res.data || [];
      const names = kids.map(k => k.name).join(' et ');
      setAllChildNames(names);

      if (kids.length === 0) {
        setError("no-children");
        setLoading(false);
        return;
      }

      // Auto-sélection : pas d'écran de choix
      let selectedChildId;

      if (childIdFromUrl) {
        // child_id fourni dans l'URL
        selectedChildId = childIdFromUrl;
      } else if (kids.length === 1) {
        // Un seul enfant
        selectedChildId = kids[0].id;
      } else {
        // Plusieurs enfants : déterminer la tranche
        const brackets = [...new Set(kids.map(k => getAgeBracket(k.age)))];
        if (brackets.length === 1) {
          // Tous dans la même tranche → premier enfant
          selectedChildId = kids[0].id;
        } else {
          // Tranches différentes → prendre un enfant "grands" (5+)
          const grandKid = kids.find(k => getAgeBracket(k.age) === 'large');
          selectedChildId = grandKid ? grandKid.id : kids[0].id;
        }
      }

      await loadQuiz(selectedChildId);
    } catch (e) {
      setError("load-error");
      setLoading(false);
    }
  };

  const loadQuiz = async (childId) => {
    setLoading(true);
    try {
      const res = await api.get(`/quiz/newsletter/${newsletterId}?child_id=${childId}`);
      setQuestions(res.data.questions || []);
      setNewsletterQuestion(res.data.newsletter_question || '');
      setChildName(res.data.child_name || '');
      setLoading(false);
    } catch (e) {
      setError("quiz-error");
      setLoading(false);
    }
  };

  const handleAnswer = useCallback((idx) => {
    if (showResult && isCorrect) return;

    const correct = idx === questions[currentIndex]?.correct_index;
    setSelectedAnswer(idx);

    if (correct) {
      setIsCorrect(true);
      setShowResult(true);
      setShowBravo(true);
      setTimeout(() => {
        setShowBravo(false);
        setTimeout(() => {
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(i => i + 1);
            setSelectedAnswer(null);
            setShowResult(false);
            setIsCorrect(false);
            setAttempts(0);
          } else {
            setFinished(true);
          }
        }, 300);
      }, 1500);
    } else {
      setShakeIdx(idx);
      setAttempts(a => a + 1);
      setTimeout(() => setShakeIdx(null), 500);
      if (attempts >= 1) {
        setShowResult(true);
        setTimeout(() => {
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(i => i + 1);
            setSelectedAnswer(null);
            setShowResult(false);
            setIsCorrect(false);
            setAttempts(0);
          } else {
            setFinished(true);
          }
        }, 1500);
      }
    }
  }, [showResult, isCorrect, questions, currentIndex, attempts]);

  // Auto-redirect après "Bravo" final
  useEffect(() => {
    if (finished) {
      const fadeTimer = setTimeout(() => setFadeOut(true), 1800);
      const navTimer = setTimeout(() => navigate('/chat'), 2500);
      return () => { clearTimeout(fadeTimer); clearTimeout(navTimer); };
    }
  }, [finished, navigate]);

  const ctaText = allChildNames
    ? `${allChildNames} ${allChildNames.includes(' et ') ? 'ont' : 'a'} plein de questions !`
    : 'Poser une question !';

  const bgStyle = {
    background: 'linear-gradient(180deg, #B8D4E3 0%, #A7D3D8 30%, #9ECDC4 60%, #B5DCAE 100%)',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden'
  };

  // Loading
  if (loading) {
    return (
      <div style={bgStyle} className="flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/80 text-sm">Chargement du quiz...</p>
        </div>
        <img src={MASCOT_URL} alt="" className="absolute bottom-0 right-0 w-48 opacity-30 pointer-events-none" />
      </div>
    );
  }

  // Error / not connected
  if (error) {
    return (
      <div style={bgStyle} className="flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-md rounded-3xl p-8 text-center max-w-sm shadow-xl">
          <p className="text-gray-600 mb-4 text-sm">
            {error === 'connect' ? 'Connectez-vous pour accéder au quiz' :
             error === 'no-children' ? 'Aucun profil enfant trouvé' :
             'Quiz non disponible'}
          </p>
          <button onClick={() => navigate('/login')} className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-6 py-3 rounded-full font-bold text-sm shadow-lg" data-testid="quiz-login-btn">
            Se connecter
          </button>
        </div>
        <img src={MASCOT_URL} alt="" className="absolute bottom-0 right-0 w-48 opacity-30 pointer-events-none" />
      </div>
    );
  }

  // Finished — Bravo avec fade-out + auto-redirect
  if (finished) {
    return (
      <div style={bgStyle} className="flex items-center justify-center p-4">
        <style>{`
          @keyframes bravoScale {
            0% { opacity: 0; transform: scale(0.3); }
            50% { opacity: 1; transform: scale(1.1); }
            70% { transform: scale(0.95); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes bravoFadeOut {
            0% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(0.8) translateY(-30px); }
          }
          .bravo-enter { animation: bravoScale 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
          .bravo-exit { animation: bravoFadeOut 0.6s ease-in forwards; }
        `}</style>

        <div className={`text-center max-w-sm ${fadeOut ? 'bravo-exit' : 'bravo-enter'}`} data-testid="quiz-finished">
          <div className="text-5xl mb-3">👍</div>
          <h2 className="text-5xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Caveat', cursive" }}>
            Bravo !
          </h2>
          <p className="text-gray-600 text-sm mb-4">{ctaText}</p>
          <button
            onClick={() => navigate('/chat')}
            className="bg-white/85 backdrop-blur-md text-gray-800 px-8 py-4 rounded-2xl font-bold text-base shadow-xl"
            data-testid="quiz-go-home"
          >
            Poser une question
          </button>
        </div>
        <img src={MASCOT_URL} alt="" className="absolute bottom-0 right-4 w-56 opacity-30 pointer-events-none" />
      </div>
    );
  }

  const q = questions[currentIndex];
  if (!q) {
    return (
      <div style={bgStyle} className="flex items-center justify-center">
        <p className="text-white/80">Aucune question disponible</p>
      </div>
    );
  }

  return (
    <div style={bgStyle} className="p-4 pb-24">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .shake-anim { animation: shake 0.4s ease-in-out; }
      `}</style>

      {/* Close button */}
      <button onClick={() => navigate('/chat')} className="absolute top-4 left-4 w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center shadow-md z-10" data-testid="quiz-close-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      <div className="max-w-md mx-auto mt-12">
        {/* Header */}
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-3" style={{ fontFamily: "'Caveat', cursive" }} data-testid="quiz-title">
          Quiz de la semaine
        </h2>
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="text-sm font-bold text-gray-700">Question {currentIndex + 1}/{questions.length}</span>
          <div className="flex gap-1.5">
            {questions.map((_, i) => (
              <div key={i} className={`w-3 h-3 rounded-full transition-colors ${i <= currentIndex ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white/85 backdrop-blur-md rounded-3xl p-5 shadow-xl mb-4" data-testid="quiz-question-card">
          <h3 className="text-xl font-bold text-gray-800 text-center leading-snug" data-testid="quiz-question-text">
            {q.question}
          </h3>
        </div>

        {/* Answer options */}
        <div className="space-y-3">
          {(q.options || []).map((option, idx) => {
            let bgClass = 'bg-white/80 backdrop-blur-md text-gray-800';
            let extraClass = '';

            if (showResult && isCorrect && idx === q.correct_index) {
              bgClass = 'bg-green-100/90 backdrop-blur-md text-green-800 border-2 border-green-400';
            } else if (showResult && !isCorrect && idx === q.correct_index) {
              bgClass = 'bg-green-100/90 backdrop-blur-md text-green-800 border-2 border-green-400';
            } else if (selectedAnswer === idx && !isCorrect) {
              bgClass = 'bg-red-100/80 backdrop-blur-md text-red-700';
            }

            if (shakeIdx === idx) {
              extraClass = 'shake-anim';
            }

            const isDisabled = showResult && isCorrect;

            return (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                disabled={isDisabled}
                className={`w-full text-left p-4 rounded-2xl shadow-md transition-all text-base font-medium ${bgClass} ${extraClass}`}
                data-testid={`quiz-option-${idx}`}
              >
                {option}
              </button>
            );
          })}
        </div>

        {/* Animation "Génial!" — pouces qui s'envolent */}
        {showBravo && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="text-center animate-bounce">
              <div className="text-5xl mb-2">👍</div>
              <div 
                className="text-3xl font-bold text-[#4CAF50]"
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                Génial !
              </div>
            </div>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="absolute text-2xl animate-ping"
                style={{
                  left: `${30 + i * 20}%`,
                  top: '40%',
                  animationDuration: `${0.5 + i * 0.1}s`,
                  animationDelay: `${i * 0.15}s`
                }}
              >
                👍
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mascotte */}
      <img src={MASCOT_URL} alt="" className="absolute bottom-0 right-0 w-48 opacity-25 pointer-events-none" />
    </div>
  );
};

export default NewsletterQuizPage;
